import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const monthLabelForPdf = (ym) => {
  const [y, m] = (ym || "").slice(0, 7).split("-");
  return new Date(Number(y), Number(m) - 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const fmt = (n) => (Number(n) || 0).toLocaleString("en-IN");

function achievementTier(pct) {
  if (pct >= 100)
    return { label: "Target Met", color: [16, 128, 71], bg: [220, 252, 231] };
  if (pct >= 70)
    return { label: "On Track", color: [161, 98, 7], bg: [254, 243, 199] };
  return { label: "Below Target", color: [185, 28, 28], bg: [254, 226, 226] };
}

function drawMetricCard(doc, x, y, w, h, label, value, accentRgb) {
  doc.setFillColor(248, 250, 253);
  doc.setDrawColor(228, 235, 245);
  doc.roundedRect(x, y, w, h, 6, 6, "FD");

  doc.setFillColor(...accentRgb);
  doc.roundedRect(x, y, w, 4, 6, 6, "F");
  doc.rect(x, y + 2, w, 2, "F");

  doc.setFontSize(8.5);
  doc.setTextColor(110, 122, 145);
  doc.text(label.toUpperCase(), x + 10, y + 20);

  doc.setFontSize(17);
  doc.setTextColor(30, 58, 95);
  doc.setFont(undefined, "bold");
  doc.text(String(value), x + 10, y + 38);
  doc.setFont(undefined, "normal");
}

function buildOperatorSummaryTable(
  doc,
  pairings,
  managerName,
  marginX,
  startY,
) {
  if (!Array.isArray(pairings) || pairings.length === 0) return startY;

  const byOperator = {};
  pairings.forEach((p) => {
    const name = p.operator_name || "Unknown";
    if (!byOperator[name]) byOperator[name] = { issued: 0, achieved: 0 };
    byOperator[name].issued += 1;
    if (p.status === "Achieved") byOperator[name].achieved += 1;
  });

  const operatorNames = Object.keys(byOperator).sort();

  doc.setFontSize(11);
  doc.setTextColor(30, 58, 95);
  doc.setFont(undefined, "bold");
  doc.text(`${managerName} \u2014 Operator-wise Achievement`, marginX, startY);
  doc.setFont(undefined, "normal");

  let totalIssued = 0;
  let totalAchieved = 0;

  const body = operatorNames.map((name) => {
    const { issued, achieved } = byOperator[name];
    totalIssued += issued;
    totalAchieved += achieved;
    const pct = issued ? Math.round((achieved / issued) * 100) : 0;
    return [name, fmt(issued), fmt(achieved), `${pct}%`];
  });

  const totalPct = totalIssued
    ? Math.round((totalAchieved / totalIssued) * 100)
    : 0;

  body.push([
    { content: "TOTAL", styles: { fontStyle: "bold" } },
    { content: fmt(totalIssued), styles: { fontStyle: "bold" } },
    { content: fmt(totalAchieved), styles: { fontStyle: "bold" } },
    { content: `${totalPct}%`, styles: { fontStyle: "bold" } },
  ]);

  autoTable(doc, {
    startY: startY + 10,
    head: [["Operator", "STB Issued", "Achieved", "Achievement %"]],
    body,
    theme: "grid",
    headStyles: {
      fillColor: [0, 163, 217],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
    },
    bodyStyles: { fontSize: 9, textColor: [30, 58, 95] },
    alternateRowStyles: { fillColor: [246, 249, 253] },
    columnStyles: {
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "center" },
    },
    margin: { left: marginX, right: marginX },
  });

  return doc.lastAutoTable.finalY + 20;
}

function buildMonthDetailPdf(doc, { row, managerName, pairings }) {
  const target = Number(row.target) || 0;
  const achieved = Number(row.achieved) || 0;
  const stbIssued = Number(row.stb_issued) || 0;
  const stbToBeIssued = Number(row.stb_tobe_issued) || 0;
  const pct = target ? Math.round((achieved / target) * 100) : 0;
  const tier = achievementTier(pct);

  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 40;

  doc.setFontSize(18);
  doc.setTextColor(15, 58, 125);
  doc.setFont(undefined, "bold");
  doc.text("Sales Overview \u2014 Monthly Breakup", marginX, 46);
  doc.setFont(undefined, "normal");

  doc.setFontSize(12);
  doc.setTextColor(60, 72, 95);
  doc.text(`${managerName}`, marginX, 66);

  doc.setFontSize(11);
  doc.setTextColor(120, 132, 155);
  doc.text(monthLabelForPdf(row.month), marginX, 82);

  doc.setFontSize(9);
  doc.setTextColor(150, 160, 175);
  doc.text(`Generated ${new Date().toLocaleString()}`, pageW - marginX, 46, {
    align: "right",
  });

  doc.setFillColor(...tier.bg);
  doc.roundedRect(pageW - marginX - 100, 58, 100, 22, 11, 11, "F");
  doc.setFontSize(10);
  doc.setTextColor(...tier.color);
  doc.setFont(undefined, "bold");
  doc.text(`${tier.label} \u00b7 ${pct}%`, pageW - marginX - 50, 72, {
    align: "center",
  });
  doc.setFont(undefined, "normal");

  const cardsY = 100;
  const cardH = 52;
  const gap = 12;
  const cardW = (pageW - marginX * 2 - gap * 3) / 4;

  drawMetricCard(
    doc,
    marginX,
    cardsY,
    cardW,
    cardH,
    "Target",
    fmt(target),
    [15, 58, 125],
  );
  drawMetricCard(
    doc,
    marginX + (cardW + gap),
    cardsY,
    cardW,
    cardH,
    "STB Issued",
    fmt(stbIssued),
    [0, 163, 217],
  );
  drawMetricCard(
    doc,
    marginX + (cardW + gap) * 2,
    cardsY,
    cardW,
    cardH,
    "To Be Issued",
    fmt(stbToBeIssued),
    [245, 158, 11],
  );
  drawMetricCard(
    doc,
    marginX + (cardW + gap) * 3,
    cardsY,
    cardW,
    cardH,
    "Achieved",
    fmt(achieved),
    pct >= 70 ? [16, 185, 129] : [239, 68, 68],
  );

  autoTable(doc, {
    startY: cardsY + cardH + 26,
    head: [["Metric", "Value", "Notes"]],
    body: [
      [
        "Target (STB count)",
        fmt(target),
        "Monthly target set for this manager",
      ],
      ["STB To Be Issued", fmt(stbToBeIssued), "Pending allotment"],
      ["STB Issued", fmt(stbIssued), "Set-top boxes physically issued"],
      ["Achieved", fmt(achieved), "Confirmed sales against target"],
      ["Achievement Rate", `${pct}%`, tier.label],
    ],
    theme: "grid",
    headStyles: {
      fillColor: [15, 58, 125],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    bodyStyles: { fontSize: 10, textColor: [30, 58, 95] },
    alternateRowStyles: { fillColor: [246, 249, 253] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 170 },
      1: { halign: "center", cellWidth: 90 },
    },
    margin: { left: marginX, right: marginX },
  });

  buildOperatorSummaryTable(
    doc,
    pairings,
    managerName,
    marginX,
    doc.lastAutoTable.finalY + 26,
  );

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(160, 170, 185);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageW - marginX,
      doc.internal.pageSize.getHeight() - 20,
      { align: "right" },
    );
  }
}

function buildFullHistoryPdf(doc, { rows, managerName }) {
  doc.setFontSize(16);
  doc.setTextColor(15, 58, 125);
  doc.text("Sales Overview", 40, 42);

  doc.setFontSize(11);
  doc.setTextColor(90, 100, 120);
  doc.text(`${managerName} \u00b7 Monthly Details`, 40, 60);

  doc.setFontSize(9);
  doc.setTextColor(140, 150, 165);
  doc.text(`Generated ${new Date().toLocaleString()}`, 40, 74);

  const body = rows.map((r) => {
    const target = Number(r.target) || 0;
    const achieved = Number(r.achieved) || 0;
    const pct = target ? Math.round((achieved / target) * 100) : 0;
    return [
      monthLabelForPdf(r.month),
      fmt(target),
      fmt(r.stb_tobe_issued),
      fmt(r.stb_issued),
      fmt(achieved),
      `${pct}%`,
    ];
  });

  autoTable(doc, {
    startY: 90,
    head: [
      [
        "Month",
        "Target",
        "STB To Be Issued",
        "STB Issued",
        "Achieved",
        "Achievement",
      ],
    ],
    body,
    theme: "grid",
    headStyles: {
      fillColor: [15, 58, 125],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
    },
    bodyStyles: { halign: "center", fontSize: 10, textColor: [30, 58, 95] },
    alternateRowStyles: { fillColor: [246, 249, 253] },
    columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
    didParseCell: (hookData) => {
      if (hookData.section === "body" && hookData.column.index === 5) {
        const pct = parseInt(hookData.cell.raw, 10);
        if (pct >= 100) hookData.cell.styles.textColor = [16, 128, 71];
        else if (pct >= 70) hookData.cell.styles.textColor = [161, 98, 7];
        else hookData.cell.styles.textColor = [185, 28, 28];
        hookData.cell.styles.fontStyle = "bold";
      }
    },
  });
}

function drawManagerOperatorBlock(
  doc,
  managerName,
  operators,
  marginX,
  startY,
) {
  if (!Array.isArray(operators) || operators.length === 0) return startY;

  const pageH = doc.internal.pageSize.getHeight();

  if (startY > pageH - 120) {
    doc.addPage();
    startY = 40;
  }

  doc.setFontSize(10.5);
  doc.setTextColor(30, 58, 95);
  doc.setFont(undefined, "bold");
  doc.text(managerName, marginX, startY);
  doc.setFont(undefined, "normal");

  let totalIssued = 0;
  let totalAchieved = 0;

  const body = operators.map((op) => {
    const issued = Number(op.stb_issued) || 0;
    const achieved = Number(op.achieved) || 0;
    totalIssued += issued;
    totalAchieved += achieved;
    const pct = issued ? Math.round((achieved / issued) * 100) : 0;
    return [op.operator_name || "-", fmt(issued), fmt(achieved), `${pct}%`];
  });

  const totalPct = totalIssued
    ? Math.round((totalAchieved / totalIssued) * 100)
    : 0;

  body.push([
    { content: "Subtotal", styles: { fontStyle: "bold" } },
    { content: fmt(totalIssued), styles: { fontStyle: "bold" } },
    { content: fmt(totalAchieved), styles: { fontStyle: "bold" } },
    { content: `${totalPct}%`, styles: { fontStyle: "bold" } },
  ]);

  autoTable(doc, {
    startY: startY + 8,
    head: [["Operator", "STB Issued", "Achieved", "Achievement %"]],
    body,
    theme: "grid",
    headStyles: {
      fillColor: [0, 163, 217],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8.5,
    },
    bodyStyles: { fontSize: 8.5, textColor: [30, 58, 95] },
    alternateRowStyles: { fillColor: [246, 249, 253] },
    columnStyles: {
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "center" },
    },
    margin: { left: marginX, right: marginX },
  });

  return doc.lastAutoTable.finalY + 18;
}

function buildAllManagersSummaryPdf(
  doc,
  { managerRows, month, operatorDetails },
) {
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 40;

  doc.setFontSize(18);
  doc.setTextColor(15, 58, 125);
  doc.setFont(undefined, "bold");
  doc.text("Sales Overview \u2014 All Managers", marginX, 46);
  doc.setFont(undefined, "normal");

  doc.setFontSize(12);
  doc.setTextColor(60, 72, 95);
  doc.text(monthLabelForPdf(month), marginX, 66);

  doc.setFontSize(9);
  doc.setTextColor(150, 160, 175);
  doc.text(`Generated ${new Date().toLocaleString()}`, pageW - marginX, 46, {
    align: "right",
  });

  let totalStbIssued = 0;
  let totalToBeIssued = 0;
  let totalTarget = 0;
  let totalAchieved = 0;

  const body = managerRows.map((m) => {
    const target = Number(m.target) || 0;
    const stbIssued = Number(m.stb_issued) || 0;
    const stbTobe = Number(m.stb_tobe_issued) || 0;
    const achieved = Number(m.achieved) || 0;
    const pct = target ? Math.round((achieved / target) * 100) : 0;

    totalStbIssued += stbIssued;
    totalToBeIssued += stbTobe;
    totalTarget += target;
    totalAchieved += achieved;

    return [
      m.name || "-",
      fmt(stbIssued),
      fmt(stbTobe),
      fmt(target),
      fmt(achieved),
      `${pct}%`,
    ];
  });

  const totalPct = totalTarget
    ? Math.round((totalAchieved / totalTarget) * 100)
    : 0;

  const totalRowIndex = body.length;
  body.push([
    { content: "TOTAL", styles: { fontStyle: "bold" } },
    { content: fmt(totalStbIssued), styles: { fontStyle: "bold" } },
    { content: fmt(totalToBeIssued), styles: { fontStyle: "bold" } },
    { content: fmt(totalTarget), styles: { fontStyle: "bold" } },
    { content: fmt(totalAchieved), styles: { fontStyle: "bold" } },
    { content: `${totalPct}%`, styles: { fontStyle: "bold" } },
  ]);

  autoTable(doc, {
    startY: 90,
    head: [
      [
        "Name",
        "STB Issued",
        "To Be Issued",
        "Target",
        "Achieved",
        "Achievement %",
      ],
    ],
    body,
    theme: "grid",
    headStyles: {
      fillColor: [15, 58, 125],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
    },
    bodyStyles: { halign: "center", fontSize: 10, textColor: [30, 58, 95] },
    alternateRowStyles: { fillColor: [246, 249, 253] },
    columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
    didParseCell: (hookData) => {
      if (
        hookData.section === "body" &&
        hookData.column.index === 5 &&
        hookData.row.index < totalRowIndex
      ) {
        const pct = parseInt(hookData.cell.raw, 10);
        if (pct >= 100) hookData.cell.styles.textColor = [16, 128, 71];
        else if (pct >= 70) hookData.cell.styles.textColor = [161, 98, 7];
        else hookData.cell.styles.textColor = [185, 28, 28];
        hookData.cell.styles.fontStyle = "bold";
      }
    },
    margin: { left: marginX, right: marginX },
  });

  if (Array.isArray(operatorDetails) && operatorDetails.length > 0) {
    doc.addPage();
    let y = 46;
    doc.setFontSize(14);
    doc.setTextColor(15, 58, 125);
    doc.setFont(undefined, "bold");
    doc.text("Operator-wise Achievement by Manager", marginX, y);
    doc.setFont(undefined, "normal");
    y += 24;

    operatorDetails.forEach((m) => {
      if (!m.operators || m.operators.length === 0) return;
      y = drawManagerOperatorBlock(doc, m.name, m.operators, marginX, y);
    });
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(160, 170, 185);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageW - marginX,
      doc.internal.pageSize.getHeight() - 20,
      { align: "right" },
    );
  }
}

/**
 * @param {Object} params
 * @param {Array}  params.rows
 * @param {string} params.managerName
 * @param {string} [params.singleMonth]
 * @param {Array}  [params.pairings]
 */
export function exportSalesOverviewPdf({
  rows,
  managerName,
  singleMonth,
  pairings,
}) {
  if (!rows?.length) throw new Error("No data available to export");

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

  if (singleMonth) {
    const row = rows.find((r) => r.month === singleMonth);
    if (!row) throw new Error("No data found for that month");
    buildMonthDetailPdf(doc, { row, managerName, pairings });
    doc.save(
      `sales-overview-${(managerName || "manager").replace(/\s+/g, "_")}-${singleMonth}.pdf`,
    );
  } else {
    buildFullHistoryPdf(doc, { rows, managerName });
    doc.save(
      `sales-overview-${(managerName || "manager").replace(/\s+/g, "_")}.pdf`,
    );
  }
}

/**
 * @param {Object} params
 * @param {Array}  params.managerRows
 *
 *
 * @param {string} params.month
 * @param {Array}  [params.operatorDetails]
 *
 */
export function exportSalesOverviewAllManagersPdf({
  managerRows,
  month,
  operatorDetails,
}) {
  if (!managerRows?.length) throw new Error("No data available to export");

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  buildAllManagersSummaryPdf(doc, { managerRows, month, operatorDetails });
  doc.save(`sales-overview-all-managers-${month}.pdf`);
}
